import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import ScrollTopButton from "@/components/feature/ScrollTopButton";
import Breadcrumbs from "@/components/base/Breadcrumbs";
import PostCard from "@/components/base/PostCard";
import usePageMeta from "@/hooks/usePageMeta";
import { categories } from "@/mocks/categories";
import { posts } from "@/mocks/posts";
import { site } from "@/mocks/site";

interface CategoryPageProps {
  slug: string;
}

export default function CategoryPage({ slug }: CategoryPageProps) {
  const category = categories.find((item) => item.slug === slug) ?? categories[0];
  const categoryPosts = useMemo(() => posts.filter((post) => post.category === slug), [slug]);

  const [activeTag, setActiveTag] = useState("全部");
  const [sort, setSort] = useState<"latest" | "hot">("latest");
  const [keyword, setKeyword] = useState("");
  const [visible, setVisible] = useState(6);

  const allTags = useMemo(() => {
    const set = new Set<string>();
    categoryPosts.forEach((post) => post.tags.forEach((tag) => set.add(tag)));
    return ["全部", ...Array.from(set)];
  }, [categoryPosts]);

  const filtered = useMemo(() => {
    const key = keyword.trim();
    const list = categoryPosts.filter((post) => {
      const tagOk = activeTag === "全部" || post.tags.includes(activeTag);
      const keyOk =
        !key ||
        post.title.includes(key) ||
        post.excerpt.includes(key) ||
        post.tags.some((tag) => tag.includes(key));
      return tagOk && keyOk;
    });
    return sort === "hot" ? [...list].sort((a, b) => b.likes - a.likes) : list;
  }, [categoryPosts, activeTag, keyword, sort]);

  const shown = filtered.slice(0, visible);
  const hotList = useMemo(() => [...categoryPosts].sort((a, b) => b.views - a.views).slice(0, 5), [categoryPosts]);
  const otherCategories = categories.filter((item) => item.slug !== slug);

  const title = `${category.name} - 网爆吃瓜${category.name}频道`;
  const description = `${site.brand}${category.name}频道，${category.desc}。收录网爆吃瓜${category.name}、吃瓜群众评论与神回复，${site.updateTime}。`;

  usePageMeta({
    title,
    description,
    keywords: `网爆吃瓜${category.name},吃瓜${category.name},网爆吃瓜,${category.name},吃瓜群众评论,神回复`,
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        name: title,
        description,
        inLanguage: "zh-CN",
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: "/" },
          { "@type": "ListItem", position: 2, name: category.name },
        ],
      },
      {
        "@context": "https://schema.org",
        "@type": "ItemList",
        name: `网爆吃瓜${category.name}内容列表`,
        itemListElement: categoryPosts.slice(0, 10).map((post, index) => ({
          "@type": "ListItem",
          position: index + 1,
          name: post.title,
        })),
      },
    ],
  });

  return (
    <div className="min-h-screen bg-background-50">
      <SiteHeader />
      <main>
        <div className="bg-background-100/70 border-b border-background-200/80">
          <div className="container-page">
            <Breadcrumbs items={[{ label: "首页", path: "/" }, { label: category.name }]} />
            <div className="pb-10 md:pb-14 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
              <div className="max-w-2xl">
                <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-background-50 text-primary-700 text-xs border border-background-200 whitespace-nowrap">
                  <span className="w-3.5 h-3.5 flex items-center justify-center">
                    <i className={category.icon}></i>
                  </span>
                  频道 · {category.count} 条内容
                </span>
                <h1 className="mt-4 text-3xl md:text-4xl text-foreground-950">{category.name}</h1>
                <p className="mt-4 text-sm md:text-base text-foreground-600 leading-relaxed">{category.intro}</p>
              </div>
              <div className="flex items-center gap-3">
                <div className="flex items-center h-10 w-full sm:w-64 rounded-md border border-background-300 bg-background-50 px-3 focus-within:border-primary-400 transition-colors">
                  <span className="w-4 h-4 flex items-center justify-center text-foreground-400">
                    <i className="ri-search-line"></i>
                  </span>
                  <input
                    value={keyword}
                    onChange={(event) => {
                      setKeyword(event.target.value);
                      setVisible(6);
                    }}
                    placeholder={`搜${category.name}`}
                    aria-label={`搜索${category.name}`}
                    className="flex-1 bg-transparent border-0 outline-none text-sm px-2 text-foreground-900 placeholder:text-foreground-400"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="container-page py-8 md:py-12 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10">
          <div className="lg:col-span-8">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
              <div className="flex flex-wrap items-center gap-2">
                {allTags.slice(0, 6).map((tag) => (
                  <button
                    key={tag}
                    type="button"
                    onClick={() => {
                      setActiveTag(tag);
                      setVisible(6);
                    }}
                    className={`px-3 py-1.5 rounded-full text-xs whitespace-nowrap transition-colors cursor-pointer border ${
                      activeTag === tag
                        ? "bg-primary-500 text-background-50 border-primary-500"
                        : "bg-background-50 text-foreground-600 border-background-200 hover:border-primary-300"
                    }`}
                  >
                    {tag}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-1 p-1 rounded-full bg-background-100 border border-background-200 self-start">
                <button
                  type="button"
                  onClick={() => setSort("latest")}
                  className={`px-3 py-1.5 rounded-full text-xs whitespace-nowrap transition-colors cursor-pointer ${
                    sort === "latest" ? "bg-background-50 text-foreground-950" : "text-foreground-500"
                  }`}
                >
                  最新
                </button>
                <button
                  type="button"
                  onClick={() => setSort("hot")}
                  className={`px-3 py-1.5 rounded-full text-xs whitespace-nowrap transition-colors cursor-pointer ${
                    sort === "hot" ? "bg-background-50 text-foreground-950" : "text-foreground-500"
                  }`}
                >
                  最热
                </button>
              </div>
            </div>

            {shown.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-5">
                {shown.map((post) => (
                  <PostCard key={post.id} post={post} />
                ))}
              </div>
            ) : (
              <div className="rounded-lg border border-background-200 bg-background-50 px-6 py-16 text-center">
                <span className="mx-auto w-12 h-12 rounded-full bg-background-100 text-foreground-400 flex items-center justify-center text-2xl">
                  <i className="ri-emotion-unhappy-line"></i>
                </span>
                <p className="mt-4 text-sm text-foreground-600">没有找到匹配的内容，换个关键词再试试</p>
                <button
                  type="button"
                  onClick={() => {
                    setKeyword("");
                    setActiveTag("全部");
                  }}
                  className="mt-4 inline-flex items-center h-9 px-4 rounded-md bg-primary-500 text-background-50 text-sm hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  重置筛选
                </button>
              </div>
            )}

            {visible < filtered.length ? (
              <div className="mt-8 flex justify-center">
                <button
                  type="button"
                  onClick={() => setVisible((count) => count + 6)}
                  className="inline-flex items-center gap-2 h-11 px-6 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-800 hover:border-primary-400 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-down-line"></i>
                  </span>
                  加载更多（{filtered.length - visible}）
                </button>
              </div>
            ) : null}
          </div>

          <aside className="lg:col-span-4 flex flex-col gap-6">
            <section className="rounded-lg border border-background-200 bg-background-50 p-5">
              <h2 className="text-base text-foreground-950 flex items-center gap-2">
                <span className="w-5 h-5 flex items-center justify-center text-primary-500">
                  <i className="ri-fire-line"></i>
                </span>
                频道热榜
              </h2>
              <ol className="mt-4 space-y-3">
                {hotList.map((post, index) => (
                  <li key={post.id}>
                    <Link to={`/post/${post.id}`} className="group flex items-start gap-3 cursor-pointer">
                      <span
                        className={`w-5 h-5 shrink-0 rounded flex items-center justify-center text-xs ${
                          index < 3 ? "bg-primary-100 text-primary-700" : "bg-background-100 text-foreground-400"
                        }`}
                      >
                        {index + 1}
                      </span>
                      <span className="text-sm text-foreground-800 leading-snug line-clamp-2 group-hover:text-primary-600 transition-colors">
                        {post.title}
                      </span>
                    </Link>
                  </li>
                ))}
              </ol>
            </section>

            <section className="rounded-lg border border-background-200 bg-background-50 p-5">
              <h2 className="text-base text-foreground-950">换个频道看看</h2>
              <ul className="mt-4 grid grid-cols-2 gap-2">
                {otherCategories.map((item) => (
                  <li key={item.slug}>
                    <Link
                      to={`/${item.slug}`}
                      className="flex items-center gap-2 px-3 py-2.5 rounded-md bg-background-100/70 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-700 transition-colors cursor-pointer"
                    >
                      <span className="w-4 h-4 flex items-center justify-center">
                        <i className={item.icon}></i>
                      </span>
                      <span className="whitespace-nowrap">{item.name}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </section>

            <section className="rounded-lg bg-secondary-50 border border-secondary-200 p-5">
              <h2 className="text-base text-secondary-900 flex items-center gap-2">
                <span className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-send-plane-line"></i>
                </span>
                想上推荐位？
              </h2>
              <p className="mt-3 text-sm text-secondary-800 leading-relaxed">
                把你做的{category.name}内容投稿过来，被采纳后会在本频道优先展示，并标注作者署名。
              </p>
              <Link
                to="/about#submit"
                className="mt-4 inline-flex items-center gap-2 h-9 px-4 rounded-md bg-secondary-500 text-background-50 text-sm hover:bg-secondary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                去投稿
              </Link>
            </section>
          </aside>
        </div>
      </main>
      <SiteFooter />
      <ScrollTopButton />
    </div>
  );
}